import { ShortcutHint, usePageShortcut } from './shortcuts'
import { useAtomValue, useStore } from 'jotai'
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import { canUndoAtom, undo } from '@/events/atoms'
import { navigateHome } from './navigation'
import { buttonSkin } from './buttonSkin'
import { EDGE_INSET } from '@/constants/insets'
import { barMarkColor, isPale, SLIDING_LOOKS, tintToward, useSelectedLook, type SelectedLook } from './selectedLook'
import { currentPageAtom } from './pageRegistry'
import { freshStart } from './freshStart'
import { PageBand } from './pageBand'
import { useScrubPress } from './scrubPress'
import { useTrayHandle } from './trayHandle'

/**
 * Kid-scale controls for world screens. Big round targets (≥ 64 px), no text, a tactile pressed
 * state, and pointer events stopped at the control so a press never reaches the canvas.
 */

const stop = (e: ReactPointerEvent) => e.stopPropagation()

export type WorldButtonProps = {
  label: string
  active?: boolean
  onPress: () => void
  size?: number
  face?: string
  ring?: string
  children: ReactNode
  style?: CSSProperties
  /** A key that presses this button (one character); its hint shows while ⌘ is held. */
  shortcut?: string
  /** Press and drag sizes this tool (see `ToolSizeScrub`); a tap is still `onPress`. */
  scrub?: ToolSizeScrub
}

/**
 * A tool button's size scrub (docs/pages.md, "Size scrub on tool buttons"; owner, 2026-10-15: "can
 * one click and drag the eraser tool to adjust its size? same with brush"). With it, a WorldButton
 * is also its tool's size control, as `ScrubButton` is for a setting:
 *
 * - Tap: `onPress`, as ever (select the tool).
 * - Press and drag (past the 10 px dead zone): `select` (default `onPress`) first, so the tool being sized is the one
 *   in hand, then its size steps through `stops` live under the finger (src/play/scrub.ts: right
 *   and up are bigger, 28 px a stop). Pointer captured and stopped: nothing reaches the canvas.
 *   In a page band that scrolls, a sideways drag still scrolls the band; up and down size.
 * - `onAdjustingChange` is true while dragging, for `useBrushPreviewWhile`, so the brush shows in
 *   the scene at its new size; a tap never shows it.
 * - While dragging, a dot under the button shows the size (smallest to biggest).
 */
export type ToolSizeScrub = {
  /** How many sizes (2 or more). */
  stops: number
  /** The tool's size, as a stop index 0..stops-1. */
  value: number
  /** The new stop, only when it changes. */
  onChange: (stop: number) => void
  /** True while a drag is sizing the tool. */
  onAdjustingChange?: (adjusting: boolean) => void
  /**
   * What a drag does first, to put the tool in hand (default `onPress`). Give it when `onPress`
   * does more than select, e.g. toggles a card when the tool is already selected (the editor's tray).
   */
  select?: () => void
}

export function WorldButton({ label, active, onPress, size = 72, face = '#ffffff', ring = '#1d2b36', children, style, shortcut, scrub }: WorldButtonProps) {
  usePageShortcut(shortcut, onPress)
  const look = useSelectedLook()
  // The face sinks under the finger while it is down (the press is felt before the tap lands).
  const [down, setDown] = useState(false)
  const up = () => setDown(false)
  // A tool button that sizes its tool (`scrub`): the kit's scrub press, always called (hook order).
  const sizing = useScrubPress({
    stops: scrub?.stops ?? 2,
    value: scrub?.value ?? 0,
    onChange: (i) => scrub?.onChange(i),
    onTap: onPress,
    onDragStart: () => (scrub?.select ?? onPress)(),
    onAdjustingChange: (on) => scrub?.onAdjustingChange?.(on),
    adjustWhile: 'dragging',
  })
  const plainHandlers = {
    onPointerDown: (e: ReactPointerEvent) => {
      e.stopPropagation()
      if (e.pointerType !== 'mouse' || e.button === 0) setDown(true)
    },
    onPointerUp: up,
    onPointerLeave: up,
    onPointerCancel: up,
    onClick: (e: ReactMouseEvent) => {
      e.stopPropagation()
      onPress()
    },
  }
  const pressed = scrub ? sizing.down : down
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
      data-select-color={ring}
      data-scrub={scrub ? '' : undefined}
      {...(scrub ? sizing.handlers : plainHandlers)}
      style={{
        ...worldButtonStyle({ size, face, ring, active, down: pressed, look }),
        ...(scrub ? { touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' } : null),
        ...style,
      }}
    >
      {children}
      {active && look === 'dot' && <SelectedDot size={size} color={isPale(ring) ? INK : ring} />}
      {scrub && sizing.dragging && <SizeDot size={size} stop={scrub.value} stops={scrub.stops} />}
      <ShortcutHint shortcut={shortcut} />
    </button>
  )
}

/**
 * While a tool button is sizing its tool: a white badge on its lower right corner holding a dot,
 * small to big with the size (the brush itself shows in the scene, `useBrushPreviewWhile`).
 */
function SizeDot({ size, stop, stops }: { size: number; stop: number; stops: number }) {
  const t = stops > 1 ? Math.max(0, Math.min(1, stop / (stops - 1))) : 1
  const badge = Math.round(size * 0.46)
  const d = Math.round(5 + t * (badge - 11))
  return (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        right: -badge * 0.22,
        bottom: -badge * 0.22,
        width: badge,
        height: badge,
        borderRadius: badge / 2,
        background: '#ffffff',
        boxShadow: '0 2px 6px rgba(16, 32, 48, 0.3)',
        display: 'grid',
        placeItems: 'center',
        pointerEvents: 'none',
      }}
    >
      <span style={{ width: d, height: d, borderRadius: d / 2, background: INK, transition: 'width 90ms ease-out, height 90ms ease-out' }} />
    </span>
  )
}

const INK = '#1d2b36'

/** The "dot below" selected look: a small dot in the button's colour under it, inside the bar's padding. */
function SelectedDot({ size, color }: { size: number; color: string }) {
  return (
    <span
      aria-hidden
      style={{ position: 'absolute', left: '50%', top: size + 2, width: 8, height: 8, marginLeft: -4, borderRadius: 4, background: color, pointerEvents: 'none' }}
    />
  )
}

/** The skin of a selected button under each selected look (`selectedLook.ts`); unselected buttons are unchanged. */
function selectedSkin(look: SelectedLook, face: string, ring: string, down: boolean | undefined): CSSProperties {
  switch (look) {
    case 'pressed': {
      // Stays sunk into its ledge, a touch less than under the finger; no ring.
      const skin = buttonSkin({ face, state: 'pressed' })
      return down ? skin : { ...skin, transform: 'translateY(3px) scale(0.98)' }
    }
    case 'tint': {
      // The face takes a soft tint of its colour, with a slight lift; no ring.
      const skin = buttonSkin({ face: tintToward(ring, 0.3), state: down ? 'pressed' : 'rest' })
      return down ? skin : { ...skin, transform: 'translateY(-2px)' }
    }
    case 'dot':
    case 'slide':
    case 'well':
    case 'flat':
    case 'underline':
    case 'glow':
      // Marked by the dot (WorldButton) or the bar's sliding shape (WorldBar); the button itself rests.
      return buttonSkin({ face, state: down ? 'pressed' : 'rest' })
    case 'tidy':
      return buttonSkin({ face, state: down ? 'pressed' : 'selected', ring: INK, ringGap: true })
    case 'ring':
    default:
      return buttonSkin({ face, state: down ? 'pressed' : 'selected', ring })
  }
}

/**
 * WorldButton's look as a style (size, skin, pressed and selected states), for kit buttons that
 * behave differently but must look the same (`ScrubButton`). `look`: the selected look (default the ring).
 */
export function worldButtonStyle({ size = 72, face = '#ffffff', ring = '#1d2b36', active, down, look = 'ring' }: {
  size?: number
  face?: string
  ring?: string
  active?: boolean
  down?: boolean
  look?: SelectedLook
}): CSSProperties {
  return {
    width: size,
    height: size,
    borderRadius: size / 2,
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    position: 'relative',
    ...(active ? selectedSkin(look, face, ring, down) : buttonSkin({ face, state: down ? 'pressed' : 'rest' })),
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    flex: '0 0 auto',
  }
}

/**
 * Lifts the enclosing WorldBar above its neighbours while a control in it is grown out of the bar
 * (`ScrubButton`'s `adjuster`), so the grown button covers a control beside it rather than slipping under.
 * `raise(true)` / `raise(false)` in pairs; null outside a WorldBar.
 */
const WorldBarRaiseContext = createContext<((on: boolean) => void) | null>(null)
export const useWorldBarRaise = () => useContext(WorldBarRaiseContext)

/**
 * A horizontal bar of controls pinned to an edge, with a soft backing so it reads over any scene.
 * Presses and mouse moves over it stop here: a press never reaches the canvas, and the editor's
 * hover preview stays where the pointer left the scene instead of following it under the bar.
 * A top bar (`edge="top"`) is a page's main bar: it rides the kit's band (`PageBand`,
 * src/play/pageBand.tsx), between home and the corner column, scrolling when its buttons do not fit
 * (it places itself; `style` and `attach` do not apply).
 */
export function WorldBar(props: Parameters<typeof PlainWorldBar>[0]) {
  if (props.edge === 'top') return <PageBand background={props.background} accent={props.accent}>{props.children}</PageBand>
  return <PlainWorldBar {...props} />
}

function PlainWorldBar({ children, edge = 'bottom', background = 'rgba(255,255,255,0.72)', gap = 14, accent, attach, style }: {
  children: ReactNode
  /**
   * Attached to the screen's left or right edge: square on that side and flush with it, with the
   * buttons 18 px in (on the same line as the corner buttons, home and undo).
   */
  attach?: 'left' | 'right'
  /** The theme colour of the bar's sliding selection mark (default: the open page's accent). */
  accent?: string
  /** Space between the buttons (px). */
  gap?: number
  edge?: 'bottom' | 'top' | 'left' | 'right'
  background?: string
  style?: CSSProperties
}) {
  const vertical = edge === 'left' || edge === 'right'
  const pageAccent = useAtomValue(currentPageAtom)?.accent
  const markAccent = accent ?? pageAccent
  const [raised, setRaised] = useState(0)
  const raise = useCallback((on: boolean) => setRaised((n) => Math.max(0, n + (on ? 1 : -1))), [])
  // The "sliding backing" selected look: one shape behind the bar's selected button (the direct
  // child with aria-pressed), measured after each render and slid to the next one.
  const look = useSelectedLook()
  const rowRef = useRef<HTMLDivElement>(null)
  // A tray attached to an edge has a handle: tap to tuck it away, drag to move it (trayHandle.tsx).
  const rootRef = useRef<HTMLDivElement>(null)
  const tray = useTrayHandle(attach, rootRef, INK)
  const [backing, setBacking] = useState<{ x: number; y: number; w: number; h: number; color: string } | null>(null)
  useLayoutEffect(() => {
    const el = SLIDING_LOOKS.has(look) ? rowRef.current?.querySelector<HTMLElement>(':scope > [aria-pressed="true"]') : null
    const next = el ? { x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight, color: el.dataset.selectColor ?? INK } : null
    setBacking((prev) => (prev && next && prev.x === next.x && prev.y === next.y && prev.w === next.w && prev.h === next.h && prev.color === next.color) || (!prev && !next) ? prev : next)
  })
  const position: CSSProperties =
    edge === 'bottom'
      ? { left: '50%', bottom: `calc(${EDGE_INSET}px + env(safe-area-inset-bottom, 0px))`, transform: 'translateX(-50%)' }
      : edge === 'top'
        ? { left: '50%', top: `calc(${EDGE_INSET}px + env(safe-area-inset-top, 0px))`, transform: 'translateX(-50%)' }
        : edge === 'left'
          ? { left: EDGE_INSET, top: '50%', transform: 'translateY(-50%)' }
          : { right: EDGE_INSET, top: '50%', transform: 'translateY(-50%)' }
  // The backing (rounded) never clips: a rounded clip cut off the hints and rings of the end
  // buttons. Only the inner row scrolls, when a narrow screen needs it; its rectangular clip sits
  // at the backing's padding, which holds a button's lift, ring and hint.
  return (
    <div
      ref={rootRef}
      data-world-bar=""
      onPointerDown={stop}
      onPointerMove={stop}
      style={{
        position: 'absolute',
        zIndex: 20,
        // Attached: rounded only away from the edge, with the corner radius of a button plus the
        // bar's padding (a huge radius turns a tall, narrow tray into a half circle).
        borderRadius: attach === 'left' ? '0 42px 42px 0' : attach === 'right' ? '42px 0 0 42px' : 999,
        background,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        boxShadow: '0 8px 28px rgba(20, 30, 40, 0.14), inset 0 1px 0 rgba(255,255,255,0.7)',
        maxWidth: 'calc(100vw - 24px)',
        ...position,
        ...style,
        ...tray.style,
        ...(raised > 0 ? { zIndex: 30 } : null),
      }}
    >
      {tray.handle}
      <div
        ref={rowRef}
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: vertical ? 'column' : 'row',
          alignItems: 'center',
          gap,
          padding: attach === 'left' ? `12px 12px 12px ${EDGE_INSET}px` : attach === 'right' ? `12px ${EDGE_INSET}px 12px 12px` : 12,
          // A bar the page layout scales to fit (`maxWidth: 'none'` in its style) lays out whole.
          maxWidth: style?.maxWidth ?? 'calc(100vw - 24px)',
          overflowX: vertical ? undefined : 'auto',
          overflowY: vertical ? undefined : 'hidden',
          scrollbarWidth: 'none',
        }}
      >
        {backing && <SlidingMark look={look} {...backing} accent={markAccent} bar={background} />}
        <WorldBarRaiseContext.Provider value={raise}>{children}</WorldBarRaiseContext.Provider>
      </div>
    </div>
  )
}

/**
 * The sliding selected looks (`SLIDING_LOOKS`): one shape behind (or under) the bar's selected
 * button, sliding to the next one in 200 ms, ease-out. `color` is the selected button's own (its
 * ring colour); pale colours fall back to ink.
 * - slide (4): a slightly darker lozenge.
 * - well (4a): an inset slot the button sits in: inner shadow, a little darker than the bar.
 * - flat (4b): a flat lozenge in a soft tint of the colour (ink at 8% for pale ones).
 * - underline (4c): a short rounded bar, 4 px, under the button.
 * - glow (4d): a soft blurred glow of the colour behind the button.
 */
function SlidingMark({ look, x, y, w, h, color, accent, bar }: { look: SelectedLook; x: number; y: number; w: number; h: number; color: string; accent?: string; bar: string }) {
  const pale = isPale(color)
  const slideMs = 200
  const ease = 'cubic-bezier(0.2, 0.8, 0.3, 1)'
  const move = `left ${slideMs}ms ${ease}, top ${slideMs}ms ${ease}, width ${slideMs}ms ${ease}, height ${slideMs}ms ${ease}, background-color ${slideMs}ms ${ease}`
  const base: CSSProperties = { position: 'absolute', pointerEvents: 'none', transition: move }
  const around = (pad: number): CSSProperties => ({ left: x - pad, top: y - pad, width: w + 2 * pad, height: h + 2 * pad, borderRadius: 999 })
  let style: CSSProperties
  switch (look) {
    case 'well':
      style = { ...around(5), backgroundColor: barMarkColor(accent, bar, 0.14), boxShadow: 'inset 0 2px 5px rgba(16, 32, 48, 0.24), inset 0 -1px 0 rgba(255, 255, 255, 0.5)' }
      break
    case 'flat':
      style = { ...around(6), backgroundColor: pale ? barMarkColor(accent, bar, 0.22) : tintToward(color, 0.5) }
      break
    case 'underline':
      style = { left: x + w * 0.3, top: y + h + 5, width: w * 0.4, height: 4, borderRadius: 2, backgroundColor: pale ? INK : color }
      break
    case 'glow':
      // Kept within the bar's padding: a horizontal bar's row clips at it.
      style = { ...around(3), backgroundColor: pale ? 'rgba(16, 32, 48, 0.35)' : color, opacity: pale ? 1 : 0.8, filter: 'blur(5px)' }
      break
    case 'slide':
    default:
      style = { ...around(7), backgroundColor: barMarkColor(accent, bar, 0.42) }
  }
  return <div aria-hidden style={{ ...base, ...style }} />
}

/**
 * The corner column's step (px): a 60 px corner button and the 18 px gap to the one under it. The
 * top-right column (owner, 2026-10-13: "put undo under settings, and add a clear button there too")
 * is settings, then undo at one step, then clear at two (`offset`), all rendered by the host.
 */
export const CORNER_STEP = 60 + 18

/**
 * A small round corner button (home, undo, clear, settings). `offset` stacks it under another along
 * the vertical edge (`CORNER_STEP` a button). `shortcut`: one key, as WorldButton's.
 */
export function CornerButton({ label, onPress, corner, children, disabled, active, offset = 0, offsetX = 0, subtle = false, shortcut }: {
  label: string
  onPress: () => void
  corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  children: ReactNode
  disabled?: boolean
  /** On (a toggle that is open): drawn pressed in. */
  active?: boolean
  /** Extra px from the corner along the vertical edge (a button stacked under another). */
  offset?: number
  /** Extra px from the corner along the horizontal edge (a button beside another). */
  offsetX?: number
  /**
   * A small flat tab stuck to the screen edge instead of a dome: a dark frosted tab (white glyph), rounded on its
   * inner side only (like the trays attached to an edge), with the glyph at full strength (pass a
   * smaller icon), at the usual vertical position. Pressed or `active` (its panel open) it darkens.
   */
  subtle?: boolean
  shortcut?: string
}) {
  usePageShortcut(shortcut, () => { if (!disabled) onPress() })
  const [v, h] = corner.split('-') as ['top' | 'bottom', 'left' | 'right']
  const [down, setDown] = useState(false)
  const up = () => setDown(false)
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      // An open toggle closes its own popover on press; the popover's outside-press close skips it.
      data-popover-toggle={active ? '' : undefined}
      onPointerDown={(e) => {
        e.stopPropagation()
        if (!disabled && (e.pointerType !== 'mouse' || e.button === 0)) setDown(true)
      }}
      onPointerUp={up}
      onPointerLeave={up}
      onPointerCancel={up}
      onClick={(e) => {
        e.stopPropagation()
        onPress()
      }}
      style={{
        position: 'absolute',
        zIndex: 20,
        [v]: `calc(${EDGE_INSET + offset}px + env(safe-area-inset-${v}, 0px))`,
        // Clear of a phone's notch on its side (viewport-fit=cover).
        [h]: `calc(${subtle ? 0 : EDGE_INSET + offsetX}px + env(safe-area-inset-${h}, 0px))`,
        width: subtle ? 44 + EDGE_INSET : 60,
        height: 60,
        borderRadius: subtle ? 0 : 30,
        border: 'none',
        ...(subtle
          ? { background: 'transparent', boxShadow: 'none', padding: 0 }
          : buttonSkin({ state: down || active ? 'pressed' : 'rest', ledge: 4 })),
        display: 'grid',
        placeItems: 'center',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        touchAction: 'manipulation',
      }}
    >
      {subtle ? (
        <span
          style={{
            position: 'absolute',
            top: 8,
            [h]: 0,
            width: 38,
            height: 44,
            borderRadius: h === 'right' ? '22px 0 0 22px' : '0 22px 22px 0',
            display: 'grid',
            placeItems: 'center',
            color: '#ffffff',
            background: down || active ? 'rgba(10, 18, 28, 0.85)' : 'rgba(10, 18, 28, 0.5)',
            boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.12)',
            backdropFilter: 'blur(6px)',
          }}
        >
          {children}
        </span>
      ) : children}
      <ShortcutHint shortcut={shortcut} />
    </button>
  )
}

/** CSS colour of a 0xRRGGBB number. */
export const cssHex = (color: number) => `#${color.toString(16).padStart(6, '0')}`

/** The home button, top left: back to the home screen. */
export function HomeCorner() {
  return <CornerButton label="Home" corner="top-left" onPress={navigateHome}><HomeIcon /></CornerButton>
}

/**
 * The undo button, on every page in the same place (the host renders it): top right, under the
 * settings button (`slot` 1; 0 on a page with no settings button). Greyed out while there is nothing to undo. A page that
 * must land something first (an animation, an open draft) registers `registerBeforeHistoryHook`
 * (src/events/atoms.ts), which runs for this button, the camera column's undo and ⌘Z alike.
 */
export function UndoCorner({ slot = 1, icon }: { slot?: number; icon?: ReactNode }) {
  const store = useStore()
  const canUndo = useAtomValue(canUndoAtom)
  const press = useCallback(() => undo(store), [store])
  return <CornerButton label="Undo" corner="top-right" offset={slot * CORNER_STEP} disabled={!canUndo} onPress={press}>{icon ?? <UndoIcon />}</CornerButton>
}

/**
 * The clear button, on every page (the host renders it): top right, under undo (`slot` 2, or 1). Puts the world back
 * as the page made it (`freshStart`: new paper, a new sheet), one undo step, so undo brings it back.
 * Key `n`.
 */
export function ClearCorner({ slot = 2 }: { slot?: number }) {
  const store = useStore()
  const press = useCallback(() => freshStart(store), [store])
  return <CornerButton label="Start again" corner="top-right" offset={slot * CORNER_STEP} onPress={press} shortcut="n"><ClearIcon /></CornerButton>
}

/**
 * A big "start again" button for a page's own bar (owner, 2026-11-04, on the Oozery pages: "can we
 * have a reset button"): the host's corner clear (`ClearCorner`) as a full-size bar button beside
 * the colours, where a child finds it. Same action: the world back as the page made it, one undo step.
 */
export function ResetButton({ size, shortcut = 'r' }: { size?: number; shortcut?: string }) {
  const store = useStore()
  const press = useCallback(() => freshStart(store), [store])
  return (
    <WorldButton label="Reset" onPress={press} size={size} shortcut={shortcut}>
      <ResetIcon />
    </WorldButton>
  )
}

/** A circling arrow: start again. */
export function ResetIcon({ color = '#1d2b36' }: { color?: string }) {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3" />
      <path d="M4 3.5v4.2h4.2" />
    </svg>
  )
}

/**
 * A bar of colours, one big button each (ringed in its colour while selected), then any extra
 * buttons (`children`: a spade, a toggle, a fresh sheet). `selected` null: no colour is selected
 * (another tool is). Pairs with `useColorWithCard` (src/play/densityCard.tsx): `onTap={tap}`.
 */
export function ColorBar({ colors, selected, onTap, icon, label, face, background, accent, edge = 'bottom', size, style, shortcuts, scrub, children }: {
  colors: readonly number[]
  selected: number | null
  onTap: (index: number) => void
  /** The button's picture, given its colour as CSS. */
  icon: (color: string, index: number) => ReactNode
  /** Every colour button's label (what it is: "Paint", "Gel"). */
  label: string
  /** Button face (default white). */
  face?: string
  /** The bar's backing (default WorldBar's). */
  background?: string
  /** The sliding selection mark's theme colour (default: the page's accent). */
  accent?: string
  edge?: 'bottom' | 'top' | 'left' | 'right'
  size?: number
  style?: CSSProperties
  /** A key per colour (e.g. ['1', '2', '3']), as WorldButton's `shortcut`. */
  shortcuts?: readonly string[]
  /** Every colour button also sizes the brush (`ToolSizeScrub`): drag a colour to pick it and size it. */
  scrub?: ToolSizeScrub
  children?: ReactNode
}) {
  return (
    <WorldBar edge={edge} background={background} accent={accent} style={style}>
      {colors.map((c, i) => {
        const color = cssHex(c)
        return (
          <WorldButton key={c} label={label} active={i === selected} onPress={() => onTap(i)} face={face} ring={color} size={size} shortcut={shortcuts?.[i]} scrub={scrub}>
            {icon(color, i)}
          </WorldButton>
        )
      })}
      {children}
    </WorldBar>
  )
}

export function HomeIcon({ color = '#1d2b36' }: { color?: string }) {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5.5 10v9.5h13V10" />
    </svg>
  )
}

/** A fresh sheet with its corner turned and a sparkle: start again. In the ink of home and undo. */
export function ClearIcon({ color = '#1d2b36' }: { color?: string }) {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3.5h7.5l4 4v13h-11.5z" />
      <path d="M15.5 3.5v4h4" />
      <path d="M3.5 6v4M1.5 8h4" strokeWidth="2" />
    </svg>
  )
}

export function UndoIcon({ color = '#1d2b36' }: { color?: string }) {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 7 4.5 11.5 9 16" />
      <path d="M5 11.5h9a5 5 0 0 1 0 10h-2" />
    </svg>
  )
}

/**
 * A small card of options floating above the bottom bar (a tool's palette: tap the selected tool
 * again). Closes on Escape and on a press anywhere outside it except the bar (whose buttons decide
 * for themselves); that press still reaches what is under it, so a finger on the world closes the
 * card and smears in one go. Nothing to read: put big controls in it.
 */
export function WorldPopover({ open, onClose, children, background = 'rgba(255,255,255,0.9)', style }: {
  open: boolean
  onClose: () => void
  children: ReactNode
  background?: string
  style?: CSSProperties
}) {
  const ref = useRef<HTMLDivElement>(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      const t = e.target as Element | null
      if (t && (ref.current?.contains(t) || t.closest?.('[data-world-bar], [data-popover-toggle]'))) return
      closeRef.current()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current()
    }
    document.addEventListener('pointerdown', onDown, { capture: true })
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown, { capture: true })
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  if (!open) return null
  return (
    <div
      ref={ref}
      role="dialog"
      onPointerDown={stop}
      style={{
        position: 'absolute',
        zIndex: 21,
        left: '50%',
        bottom: 'calc(140px + env(safe-area-inset-bottom, 0px))',
        transform: 'translateX(-50%)',
        padding: '14px 18px',
        borderRadius: 36,
        background,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        boxShadow: '0 10px 30px rgba(20, 30, 40, 0.2)',
        maxWidth: 'calc(100vw - 24px)',
        touchAction: 'none',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/**
 * A big chunky slider with a few stops and no text: drag or tap anywhere on it. The track is a
 * wedge, thin at the start and thick at the end (more of something: heavier, slower), with a dot
 * per stop; `start` and `end` are icons at its ends. `value` is a stop index (0..stops-1);
 * `onChange` fires only when the stop changes.
 */
export function ChunkySlider({ label, stops, value, onChange, color = '#1d2b36', start, end, width = 260 }: {
  label: string
  stops: number
  value: number
  onChange: (stop: number) => void
  color?: string
  start?: ReactNode
  end?: ReactNode
  width?: number
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const dragging = useRef<number | null>(null)
  const n = Math.max(2, Math.floor(stops))
  const v = Math.max(0, Math.min(n - 1, Math.round(value)))
  const THUMB = 56
  const H = 72
  const inset = THUMB / 2
  const span = width - 2 * inset
  const xOf = (i: number) => inset + (span * i) / (n - 1)
  const set = (i: number) => {
    const c = Math.max(0, Math.min(n - 1, i))
    if (c !== v) onChange(c)
  }
  const stopAt = (clientX: number) => {
    const r = trackRef.current?.getBoundingClientRect()
    if (!r || !(r.width > 0)) return v
    const f = (clientX - r.left - inset * (r.width / width)) / (span * (r.width / width))
    return Math.round(Math.max(0, Math.min(1, f)) * (n - 1))
  }
  const onDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.preventDefault()
    dragging.current = e.pointerId
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* ignore */ }
    set(stopAt(e.clientX))
  }
  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (dragging.current !== e.pointerId) return
    e.stopPropagation()
    set(stopAt(e.clientX))
  }
  const onUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (dragging.current !== e.pointerId) return
    dragging.current = null
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* ignore */ }
  }
  const onKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); set(v - 1) }
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); set(v + 1) }
  }
  const mid = H / 2
  const thin = 5
  const thick = 26
  const wedge = (x1: number) => {
    const t = (x1 - inset) / span
    const h1 = thin + (thick - thin) * t
    return `M ${inset} ${mid - thin / 2} L ${x1} ${mid - h1 / 2} L ${x1} ${mid + h1 / 2} L ${inset} ${mid + thin / 2} Z`
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {start}
      <div
        ref={trackRef}
        role="slider"
        aria-label={label}
        title={label}
        aria-valuemin={0}
        aria-valuemax={n - 1}
        aria-valuenow={v}
        tabIndex={0}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onKeyDown={onKey}
        style={{ position: 'relative', width, height: H, cursor: 'pointer', touchAction: 'none', outline: 'none', flex: '0 0 auto' }}
      >
        <svg width={width} height={H} style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
          <path d={wedge(width - inset)} fill="rgba(29,43,54,0.14)" strokeLinejoin="round" />
          <path d={wedge(xOf(v))} fill={color} opacity={0.85} />
          {Array.from({ length: n }, (_, i) => (
            <circle key={i} cx={xOf(i)} cy={mid} r={i <= v ? 5 : 4} fill={i <= v ? '#ffffff' : 'rgba(29,43,54,0.35)'} />
          ))}
        </svg>
        <div
          style={{
            position: 'absolute',
            left: xOf(v) - THUMB / 2,
            top: mid - THUMB / 2,
            width: THUMB,
            height: THUMB,
            borderRadius: THUMB / 2,
            background: color,
            boxShadow: '0 0 0 5px #ffffff, 0 6px 14px rgba(20, 30, 40, 0.3)',
            transition: 'left 160ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            pointerEvents: 'none',
          }}
        />
      </div>
      {end}
    </div>
  )
}
